<template>
  <v-row>
    <v-breadcrumbs :items="['admin', 'price backfill']" divider="/"></v-breadcrumbs>
  </v-row>

  <!-- input card -->
  <v-row>
    <v-col cols="12" md="6">
      <v-card :elevation="1">
        <v-card-item>
          <v-card-title class="text-left">historical price backfill</v-card-title>
          <v-card-subtitle class="text-left">fetches closing prices and FX rates from Yahoo Finance for all securities held open on the selected date. FX rates are saved immediately. Security prices are shown for review before saving.</v-card-subtitle>
        </v-card-item>
        <v-divider></v-divider>
        <v-card-text class="pa-4">
          <v-row align="center">
            <v-col>
              <v-text-field
                v-model="endDate"
                type="date"
                label="date"
                density="compact"
                variant="outlined"
                hide-details
                @update:modelValue="reset"
              ></v-text-field>
            </v-col>
            <v-col cols="auto" class="text-grey text-caption">or</v-col>
            <v-col>
              <v-select
                v-model="selectedFY"
                :items="financialYears"
                label="FY shortcut (30 June)"
                density="compact"
                variant="outlined"
                hide-details
                clearable
              ></v-select>
            </v-col>
            <v-col cols="auto">
              <v-btn
                @click="fetchPrices"
                :loading="fetchLoading"
                :disabled="!endDate"
                color="primary"
              >fetch prices</v-btn>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </v-col>
  </v-row>

  <!-- results -->
  <template v-if="result">

    <!-- summary chips -->
    <v-row>
      <v-col cols="auto">
        <v-chip color="blue" variant="tonal">{{ result.tickers_found }} securities found</v-chip>
      </v-col>
      <v-chip v-if="result.prices.success.length > 0" color="orange" variant="tonal" class="ml-2">{{ result.prices.success.length }} prices fetched — review below</v-chip>
      <v-chip v-if="result.prices.skipped.length > 0" color="grey" variant="tonal" class="ml-2">{{ result.prices.skipped.length }} already backfilled</v-chip>
      <v-chip v-if="result.prices.failed.length > 0" color="red" variant="tonal" class="ml-2">{{ result.prices.failed.length }} failed</v-chip>
      <v-chip v-if="splitWarningCount > 0" color="deep-orange" variant="tonal" class="ml-2">{{ splitWarningCount }} split{{ splitWarningCount > 1 ? 's' : '' }} detected — review prices</v-chip>
      <v-chip v-if="result.fx_rates.success.length > 0" color="green" variant="tonal" class="ml-2">{{ result.fx_rates.success.length }} FX rates saved</v-chip>
      <v-chip v-if="result.fx_rates.failed.length > 0" color="red" variant="tonal" class="ml-2">{{ result.fx_rates.failed.length }} FX rates failed</v-chip>
    </v-row>

    <v-row>
      <!-- securities preview -->
      <v-col cols="12" md="8">
        <v-card :elevation="1">
          <v-card-item>
            <v-card-title class="text-left">securities</v-card-title>
            <v-card-subtitle v-if="result.prices.success.length > 0" class="text-left">review prices below — edit any that need correcting, then click save</v-card-subtitle>
          </v-card-item>
          <v-divider></v-divider>
          <v-card-text class="pa-0">
            <v-table density="compact">
              <thead>
                <tr>
                  <th>ticker</th>
                  <th class="text-right">yahoo price</th>
                  <th>split detected</th>
                  <th class="text-right" style="width:160px">price to save</th>
                  <th>status</th>
                  <th>detail</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="item in allPriceResults" :key="item.ticker">
                  <td class="font-weight-medium">{{ item.ticker }}</td>
                  <td class="text-right">{{ item.price != null ? item.price.toFixed(4) : '—' }}</td>
                  <td>
                    <template v-if="item.split_detected">
                      <v-chip size="x-small" color="deep-orange" variant="tonal">
                        {{ item.split_events.map(e => e.ratio + ' on ' + e.date).join(', ') }}
                      </v-chip>
                    </template>
                    <span v-else class="text-grey text-caption">—</span>
                  </td>
                  <td class="text-right">
                    <v-text-field
                      v-if="item.status === 'fetched'"
                      v-model.number="editablePrices[item.ticker]"
                      density="compact"
                      variant="outlined"
                      hide-details
                      type="number"
                      :class="item.split_detected ? 'split-warning-field' : ''"
                      style="min-width:120px"
                    ></v-text-field>
                    <span v-else class="text-grey text-caption">—</span>
                  </td>
                  <td>
                    <v-chip size="x-small" :color="statusColor(item.status)" variant="tonal">
                      {{ item.status }}
                    </v-chip>
                  </td>
                  <td class="text-caption text-grey">{{ item.reason || '' }}</td>
                </tr>
              </tbody>
            </v-table>
          </v-card-text>
          <v-divider v-if="result.prices.success.length > 0"></v-divider>
          <v-card-text v-if="result.prices.success.length > 0" class="pa-3 text-right">
            <v-btn
              @click="savePrices"
              :loading="saveLoading"
              color="primary"
            >save prices</v-btn>
          </v-card-text>
        </v-card>
      </v-col>

      <!-- FX results -->
      <v-col cols="12" md="4" v-if="result.fx_pairs_found > 0">
        <v-card :elevation="1">
          <v-card-item>
            <v-card-title class="text-left">FX rates</v-card-title>
            <v-card-subtitle class="text-left">saved automatically</v-card-subtitle>
          </v-card-item>
          <v-divider></v-divider>
          <v-card-text class="pa-0">
            <v-table density="compact">
              <thead>
                <tr>
                  <th>pair</th>
                  <th class="text-right">rate</th>
                  <th>status</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="item in allFxResults" :key="item.pair">
                  <td class="font-weight-medium">{{ item.pair }}</td>
                  <td class="text-right">{{ item.rate != null ? item.rate.toFixed(6) : '—' }}</td>
                  <td>
                    <v-chip size="x-small" :color="statusColor(item.status)" variant="tonal">
                      {{ item.status }}
                    </v-chip>
                  </td>
                </tr>
              </tbody>
            </v-table>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" md="4" v-else>
        <v-card :elevation="1">
          <v-card-text class="pa-4 text-grey text-caption">no foreign currency positions on this date</v-card-text>
        </v-card>
      </v-col>
    </v-row>

  </template>
</template>

<script>
import axios from 'axios';
import { mapState, mapMutations } from 'vuex';

export default {
  data() {
    return {
      selectedFY: null,
      endDate: '',
      fetchLoading: false,
      saveLoading: false,
      result: null,
      editablePrices: {}
    };
  },
  computed: {
    ...mapState({ token: 'token' }),
    financialYears() {
      const currentYear = new Date().getFullYear();
      const currentMonth = new Date().getMonth();
      const latestEndYear = currentMonth >= 6 ? currentYear : currentYear - 1;
      const years = [];
      for (let endYear = latestEndYear; endYear >= latestEndYear - 5; endYear--) {
        years.push('FY' + (endYear - 1).toString().slice(2) + '/' + endYear.toString().slice(2));
      }
      return years;
    },
    allPriceResults() {
      if (!this.result) return [];
      return [
        ...this.result.prices.success,
        ...this.result.prices.skipped,
        ...this.result.prices.failed
      ].sort((a, b) => a.ticker.localeCompare(b.ticker));
    },
    allFxResults() {
      if (!this.result) return [];
      return [...this.result.fx_rates.success, ...this.result.fx_rates.failed];
    },
    splitWarningCount() {
      if (!this.result) return 0;
      return this.result.prices.success.filter(p => p.split_detected).length;
    }
  },
  watch: {
    selectedFY(val) {
      if (!val) return;
      const parts = val.replace('FY', '').split('/');
      const endYear = 2000 + parseInt(parts[1]);
      this.endDate = endYear + '-06-30';
      this.reset();
    }
  },
  methods: {
    ...mapMutations(['showSnackbar']),
    reset() {
      this.result = null;
      this.editablePrices = {};
    },
    async fetchPrices() {
      this.fetchLoading = true;
      this.reset();
      try {
        const response = await axios.post(
          '/securities/prices/backfill',
          { date: this.endDate },
          { headers: { Authorization: `Bearer ${this.token}` } }
        );
        this.result = response.data;
        // Pre-populate editable prices from Yahoo fetched values
        for (const item of this.result.prices.success) {
          this.editablePrices[item.ticker] = item.price;
        }
      } catch (error) {
        this.showSnackbar({ message: 'Fetch failed: ' + (error.response?.data?.error || error.message) });
      }
      this.fetchLoading = false;
    },
    async savePrices() {
      this.saveLoading = true;
      try {
        const prices = Object.entries(this.editablePrices).map(([ticker, price]) => ({ ticker, price }));
        await axios.post(
          '/securities/prices/backfill/save',
          { date: this.endDate, prices },
          { headers: { Authorization: `Bearer ${this.token}` } }
        );
        this.showSnackbar({ message: `${prices.length} price${prices.length > 1 ? 's' : ''} saved successfully` });
        // Remove saved tickers from the fetch list so the save button disappears
        this.result.prices.success = [];
        this.editablePrices = {};
      } catch (error) {
        this.showSnackbar({ message: 'Save failed: ' + (error.response?.data?.error || error.message) });
      }
      this.saveLoading = false;
    },
    statusColor(status) {
      if (status === 'success') return 'green';
      if (status === 'fetched') return 'orange';
      if (status === 'skipped') return 'grey';
      return 'red';
    }
  }
};
</script>

<style scoped>
.split-warning-field :deep(.v-field) {
  border-color: rgb(var(--v-theme-deep-orange)) !important;
}
</style>
